<template>
  <div class="cart-items-list">
    <div
      v-for="(product, index) in lineItemsWithGuaranteeCombined"
      :key="product.id"
      class="first:border-0 first:pt-0 last:pb-0 border-t py-2 lg:py-4"
    >
      <ProductCardVertical
        :product="product"
        :disabled="disabled"
        :read-only="readOnly"
        :triggered-in="triggeredIn"
        @input-quantity="handleInputQuantity(product, $event)"
        @click-remove="handleClickRemove(product, index)"
      />
      <AddExtraGuarantee
        v-if="!readOnly && product.isEligibleForGuarantee"
        :product="product"
      />
      <GuaranteeParentProductCardVertical :product="product" />
    </div>
  </div>
</template>

<script lang="ts">

import debounce from 'lodash/debounce.js';
import type { LineItem, ProductVariant } from '~~/types/commercetools';
import type { LineItemExtended } from '~~/types/product/LineItemExtended';

import type {
  RemoveFromCartTagDetailsParams,
  EventSourceLocation,
} from '~~/types/integrations/tagManager/TagManagerIntegration';
import { DEBOUNCE_TIMES } from '~~/constants/debounceTimes';

export default defineComponent({
  name: 'CartItemsList',

  props: {
    readOnly: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    triggeredIn: {
      type: String,
      default: '',
    },
  },
  setup(props) {

    const router = useRouter();
    const { isCheckoutPage } = useIsPage();
    const i18n = useI18n();
    const { lineItemsWithGuaranteeCombined } = useLineItems();
    const { triggerTags: triggerTagsRemoveFromCart } = useRemoveFromCartTagManager();
    const {
      updateQuantity,
      removeItem,
    } = useCartExtended();

    const debouncedInputQuantityHandler = debounce(function(product, event) {

      updateQuantity(product, event);
    
    }, DEBOUNCE_TIMES.NUMBER_INPUT);

    const handleInputQuantity = (product: LineItem | LineItemExtended, event: Event) => {

      if (props.readOnly) {

        return;

      }

      debouncedInputQuantityHandler(product, event);
    
    };

    const handleClickRemove = async (product: LineItem | LineItemExtended, index: number) => {

      if (props.readOnly) {

        return;

      }

      await removeItem({ product });

      const productVariant: ProductVariant = {
        ...product.variant,
        price: product.price,
      };

      const details: RemoveFromCartTagDetailsParams = {
        quantity: product.quantity,
        textDisplayed: i18n.t('Remove').toString(),
        triggeredIn: props.triggeredIn as EventSourceLocation,
        index,
      };

      triggerTagsRemoveFromCart(productVariant, details);

      redirectToHomepageWhenCartIsEmpty();
    
    };

    const redirectToHomepageWhenCartIsEmpty = () => {

      if (!isCheckoutPage.value) {

        return;

      }

      if (!lineItemsWithGuaranteeCombined.value?.length) {

        router.push('/');
      
      }
    
    };

    return {
      lineItemsWithGuaranteeCombined,
      handleInputQuantity,
      handleClickRemove,
    };
  
  },
});

</script>
